// TCIO Messenger - Home widget phone (v1). An iPhone-style frame with a compact
// Messenger inside: thread list -> conversation -> send. Presence dots (2-min window).
// Self-contained: polls /api/msg directly, same endpoints as the Messages page.
import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { tokens } from '../theme';
import DictatePanel from '../DictatePanel';
import { threadLabel } from '../threadLabel';

const API = 'https://tcio-api.onrender.com';

function timeAgo(iso) {
  if (!iso) return '';
  const mins = Math.floor((new Date() - new Date(iso)) / 60000);
  if (mins < 1) return 'now';
  if (mins < 60) return mins + 'm';
  if (mins < 1440) return Math.floor(mins / 60) + 'h';
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export default function PhoneMessenger({ user }) {
  const [threads, setThreads] = useState([]);
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [online, setOnline] = useState([]);
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  const [dictOpen, setDictOpen] = useState(false);
  const [reads, setReads] = useState([]);
  const bottomRef = useRef(null);

  const loadThreads = useCallback(async () => {
    try {
      const [t, p] = await Promise.all([
        axios.get(API + '/api/msg/threads'),
        axios.get(API + '/api/msg/presence'),
      ]);
      setThreads(t.data); setOnline(p.data.online || []);
    } catch (e) { console.error(e); }
  }, []);

  const loadMessages = useCallback(async (tid) => {
    try {
      const [r, rr] = await Promise.all([
        axios.get(API + '/api/msg/threads/' + tid + '/messages'),
        axios.get(API + '/api/msg/threads/' + tid + '/reads'),
      ]);
      setMessages(r.data); setReads(rr.data);
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => { loadThreads(); const iv = setInterval(loadThreads, 15000); return () => clearInterval(iv); }, [loadThreads]);

  useEffect(() => {
    if (!active) return;
    loadMessages(active.id);
    axios.post(API + '/api/msg/threads/' + active.id + '/read').then(loadThreads).catch(() => {});
    const iv = setInterval(() => loadMessages(active.id), 6000);
    return () => clearInterval(iv);
  }, [active, loadMessages, loadThreads]);

  useEffect(() => { if (bottomRef.current) bottomRef.current.scrollIntoView({ behavior: 'smooth' }); }, [messages.length]);

  const send = async () => {
    if (!body.trim() || !active || sending) return;
    setSending(true);
    try {
      await axios.post(API + '/api/msg/threads/' + active.id + '/messages', { body: body.trim() });
      setBody('');
      await loadMessages(active.id);
    } catch (e) { console.error(e); }
    setSending(false);
  };


  // presence: for DMs the "other" side is online if the last non-me sender is online.
  const dotFor = (t) => t.last_sender_id && t.last_sender_id !== user.id && online.includes(t.last_sender_id);

  return (
    <div onClick={e => e.stopPropagation()} style={{ display: 'flex', justifyContent: 'center', padding: '4px 0' }}>
      {/* chassis */}
      <div style={{ width: 300, height: 560, background: tokens.navy950, borderRadius: 42, padding: 10,
        boxShadow: '0 8px 28px rgba(7,26,45,0.35)', position: 'relative' }}>
        {/* screen */}
        <div style={{ width: '100%', height: '100%', background: tokens.canvas, borderRadius: 30,
          overflow: 'hidden', display: 'flex', flexDirection: 'column', position: 'relative', fontFamily: tokens.font }}>
          {/* dynamic island */}
          <div style={{ position: 'absolute', top: 8, left: '50%', transform: 'translateX(-50%)',
            width: 74, height: 20, background: tokens.navy950, borderRadius: 12, zIndex: 5 }} />
          {/* status bar */}
          <div style={{ padding: '10px 18px 4px', display: 'flex', justifyContent: 'space-between',
            fontSize: 10.5, fontWeight: 700, color: tokens.ink }}>
            <span>{new Date().toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }).replace(/ [AP]M/, '')}</span>
            <span style={{ letterSpacing: 1 }}>{'\u2022\u2022\u2022'}</span>
          </div>

          {!active ? (
            <>
              <div style={{ padding: '6px 14px 8px', fontSize: 15, fontWeight: 800, color: tokens.ink }}>Messages</div>
              <div style={{ flex: 1, overflowY: 'auto' }}>
                {threads.length === 0 && (
                  <div style={{ padding: 18, fontSize: 11.5, color: tokens.textMuted, textAlign: 'center' }}>
                    No conversations yet</div>)}
                {threads.map(t => (
                  <div key={t.id} onClick={() => setActive(t)}
                    style={{ padding: '9px 14px', cursor: 'pointer', display: 'flex', gap: 9,
                      alignItems: 'center', borderBottom: '1px solid ' + tokens.borderSoft }}>
                    <div style={{ position: 'relative', flexShrink: 0 }}>
                      <div style={{ width: 32, height: 32, borderRadius: '50%', background: tokens.blue500,
                        color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 12, fontWeight: 700 }}>
                        {threadLabel(t).slice(0, 1)}</div>
                      {dotFor(t) && <div style={{ position: 'absolute', bottom: 0, right: 0, width: 9, height: 9,
                        borderRadius: '50%', background: tokens.success, border: '2px solid ' + tokens.canvas }} />}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ fontSize: 11.5, fontWeight: Number(t.unread) > 0 ? 800 : 600,
                          color: tokens.ink, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {threadLabel(t)}</span>
                        <span style={{ fontSize: 9.5, color: tokens.textMuted, flexShrink: 0 }}>{timeAgo(t.last_at)}</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 6 }}>
                        <span style={{ fontSize: 10.5, color: tokens.textSecondary, whiteSpace: 'nowrap',
                          overflow: 'hidden', textOverflow: 'ellipsis' }}>{t.last_body || ''}</span>
                        {Number(t.unread) > 0 && <span style={{ background: tokens.ai, color: '#fff',
                          borderRadius: 8, fontSize: 8.5, padding: '1px 5px', fontWeight: 700, flexShrink: 0 }}>{t.unread}</span>}
                      </div>
                    </div>
                  </div>))}
              </div>
            </>
          ) : (
            <>
              <div style={{ padding: '4px 10px 7px', display: 'flex', alignItems: 'center', gap: 7,
                borderBottom: '1px solid ' + tokens.borderSoft }}>
                <span onClick={() => setActive(null)} style={{ color: tokens.ai, fontSize: 15, fontWeight: 700,
                  cursor: 'pointer', padding: '0 3px' }}>{'<'}</span>
                <span style={{ fontSize: 12, fontWeight: 700, color: tokens.ink, whiteSpace: 'nowrap',
                  overflow: 'hidden', textOverflow: 'ellipsis' }}>{threadLabel(active)}</span>
                {dotFor(active) && <span style={{ width: 7, height: 7, borderRadius: '50%',
                  background: tokens.success, flexShrink: 0 }} />}
              </div>
              <div style={{ flex: 1, overflowY: 'auto', padding: '8px 10px' }}>
                {messages.map(m => {
                  const mine = m.sender_id === user.id;
                  return (
                    <div key={m.id} style={{ display: 'flex', justifyContent: mine ? 'flex-end' : 'flex-start',
                      marginBottom: 6 }}>
                      <div style={{ maxWidth: '78%', background: mine ? tokens.ai : '#E9E9EB',
                        color: mine ? '#fff' : tokens.ink, borderRadius: 14, padding: '6px 10px',
                        fontSize: 11.5, lineHeight: 1.4 }}>{m.body}</div>
                    </div>);
                })}
                {(() => {
                  const last = messages[messages.length - 1];
                  if (!last || last.sender_id !== user.id || !reads.length) return null;
                  const seen = reads.filter(x => x.last_read_at && new Date(x.last_read_at) >= new Date(last.created_at));
                  if (!seen.length) return null;
                  const label = reads.length === 1
                    ? 'Read ' + new Date(seen[0].last_read_at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
                    : (seen.length === reads.length ? 'Read by all' : 'Read by ' + seen.length + ' of ' + reads.length);
                  return <div style={{ textAlign: 'right', fontSize: 10.5, color: tokens.textMuted,
                    marginTop: -4, marginBottom: 6, paddingRight: 4 }}>{label}</div>;
                })()}
                <div ref={bottomRef} />
              </div>
              {dictOpen && (
                <div style={{ padding: '6px 9px 0' }}>
                  <DictatePanel fallbackPath="/api/msg/transcribe" cta="Dictate"
                    submitLabel="Use" placeholder="Speak, then edit"
                    onSubmit={(t) => { setBody(t); setDictOpen(false); }} />
                </div>
              )}
              <div style={{ padding: '7px 9px 12px', display: 'flex', gap: 6 }}>
                <button onClick={() => setDictOpen(o => !o)} title="Dictate"
                  style={{ width: 28, height: 28, borderRadius: '50%', border: '1px solid ' + tokens.border,
                    background: dictOpen ? tokens.aiSoft : tokens.surface, fontSize: 13, cursor: 'pointer',
                    padding: 0 }}>{'\uD83C\uDF99'}</button>
                <input value={body} onChange={e => setBody(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); send(); } }}
                  placeholder="iMessage"
                  style={{ flex: 1, border: '1px solid ' + tokens.border, borderRadius: 14,
                    padding: '6px 11px', fontSize: 11.5, outline: 'none', fontFamily: tokens.font }} />
                <button onClick={send} disabled={sending || !body.trim()}
                  style={{ width: 28, height: 28, borderRadius: '50%', border: 'none', background: tokens.ai,
                    color: '#fff', fontSize: 13, fontWeight: 800, cursor: 'pointer',
                    opacity: sending || !body.trim() ? 0.45 : 1 }}>{'\u2191'}</button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
